import { createStackNavigator, StackNavigationOptions } from '@react-navigation/stack'
import { FC } from 'react'

import { BottomTabNavigator } from './BottomTabNavigator'
import { WebNavigator } from './WebNavigator'
import { webScreenStyle } from './constants'

import { useAuth, useDeviceInfo, useDimensions, useMemo, useTranslation } from '~hooks'
import {
  ApplicationInfoScreen,
  NftDetailsScreen,
  OfferDetailsScreen,
  SignInScreen,
  SignUpScreen,
  UserDetailsFormScreen,
} from '~screens'
import { Routes } from '~types/routesNames'

const { Navigator, Screen, Group } = createStackNavigator<RootStackParamList>()

export const RootNavigator: FC = () => {
  const { t } = useTranslation()
  const { isSignedIn } = useAuth()
  const { isWeb, deviceSpecificWidth } = useDeviceInfo()
  const {
    window: { width: windowWidth },
  } = useDimensions()
  const screenOptionsForDesktop: StackNavigationOptions = useMemo(
    () => ({
      cardStyle: { ...webScreenStyle, paddingHorizontal: (windowWidth - deviceSpecificWidth) / 2 },
    }),
    [deviceSpecificWidth, windowWidth]
  )

  const renderTabNavigator = useMemo(() => (isWeb ? WebNavigator : BottomTabNavigator), [isWeb])

  return (
    <Navigator>
      {!isSignedIn ? (
        <Group key="unauthorized">
          <Screen
            component={SignInScreen}
            name={Routes.SignIn}
            options={{
              headerShown: false,
              title: t('navigation.screen_titles.sign_in'),
              ...(isWeb && screenOptionsForDesktop),
            }}
          />
          <Screen
            component={SignUpScreen}
            name={Routes.SignUp}
            options={{
              title: t('navigation.screen_titles.sign_up'),
            }}
          />
        </Group>
      ) : (
        <Group key="authorized" screenOptions={{ headerShown: false }}>
          <Screen
            component={renderTabNavigator}
            name={Routes.MainTab}
            options={{ title: t('navigation.screen_titles.main_tab') }}
          />
          <Group
            key="screen-without-tabBar"
            screenOptions={{
              cardStyle: {
                ...webScreenStyle,
                paddingHorizontal: (windowWidth - deviceSpecificWidth) / 2,
              },
            }}
          >
            <Screen name={Routes.NftDetails} component={NftDetailsScreen} />
            <Screen name={Routes.OfferDetails} component={OfferDetailsScreen} />
            <Screen
              component={UserDetailsFormScreen}
              name={Routes.UserDetailsForm}
              options={{
                title: t('navigation.screen_titles.user_details_form'),
              }}
            />
          </Group>
        </Group>
      )}
      <Group key="modals" screenOptions={{ presentation: 'modal' }}>
        <Screen
          component={ApplicationInfoScreen}
          name="ApplicationInfo"
          options={{ title: t('navigation.screen_titles.application_info') }}
        />
      </Group>
    </Navigator>
  )
}
